import { useState } from 'react';
import { IUserRepository } from 'repository/U006/IUserRepository';
import { UserRepository } from 'repository/U006/UserRepository';
import { FetchError } from 'api/API';
import { A03Output } from 'api/U006';

type DeleteUserRequest = {
  id: string;
  applicant_revision: number;
  user_info_revision: number;
};
type DeleteUserResponse = {
  status: string;
  msgid: string;
};

type DeleteUserDoRequest = (param: {
  request: DeleteUserRequest;
  onSuccess?: (response: DeleteUserResponse) => void;
  onError?: (data: { err: Error; res?: A03Output }) => void;
  cancellation?: AbortSignal;
}) => void;

export function useDeleteUser(): [DeleteUserDoRequest, boolean, Error | null] {
  const [loading, setLoading] = useState<boolean>(false);
  const [err, setError] = useState<Error | null>(null);

  const deleteUser = async (request: DeleteUserRequest, cancellation: AbortSignal) => {
    const repository: IUserRepository = new UserRepository();

    return await repository.delete(
      request.id,
      request.applicant_revision,
      request.user_info_revision,
      cancellation
    );
  };

  const doRequest: DeleteUserDoRequest = async ({ request, cancellation, onSuccess, onError }) => {
    const controller = new AbortController();
    const cancelSignal = cancellation ?? controller.signal;
    try {
      setLoading(true);
      setError(null);
      const user = await deleteUser(request, cancelSignal);
      onSuccess?.(user);
    } catch (err) {
      console.error(err);
      let flooredError;
      if (err instanceof FetchError) {
        flooredError = err;
        const response = (await err.response.json()) as A03Output;
        onError?.({ err: flooredError, res: response });
      } else {
        flooredError = new Error(`unknown error. ${err}`);
        onError?.({ err: flooredError });
      }
      setError(flooredError);
    } finally {
      setLoading(false);
    }
  };

  return [doRequest, loading, err];
}
